// app/src/features/timeline/useComputeVisibility.js
// Moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1; reads props and selectors, never the store.
import { useCallback, useMemo, useRef } from "react";
import { eventKey } from "../../lib/forecast/eventIdentity.js";
import { LABEL_MIN_PPD, isLongRange } from "../../lib/timeline/geometry.js";
import {
  createLabelOccupancy,
  labelPlacementOrder,
  sideCandidates,
  stickyLabelMode,
  zoomStep,
} from "../../lib/timeline/labelLayout.js";
import { clampLabelShift } from "../../lib/timeline/laneBand.js";
import { spreadCoincidentX } from "../../lib/timeline/spreadCoincident.js";

/**
 * Label visibility: which trigger labels each row shows, on which side, with hysteresis across pans
 * and zooms (the refs), plus the lane's visible edges. `computeVisibility` is the per-row pass.
 */
export function useComputeVisibility({ geom, active, railPanelSettled, railPanelWidth }) {
  const {
    cSize,
    dayToScreen,
    liveAnchorRef,
    ownsResolutionCap,
    ppd,
    promotedIds,
    rowOwnerKey,
    setSlideAnchorMs,
    slideAnchorMs,
  } = geom;

  // WrapLabel (3 × 18-char lines) is gone: labels clamp to two lines in CSS.

  // Open (clicked) labels in click order: key → rank, higher = more recent. `promotedIds`
  // is a Set, which keeps insertion order, and a click re-inserts at the end.
  const promotedOrder = useMemo(
    () => new Map([...promotedIds].map((k, i) => [k, i])),
    [promotedIds],
  );
  // key → the side (true = above) each label was shown on last pass. Hysteresis: a label
  // already on screen gets a looser overlap test and keeps its side, so panning does not
  // make labels flicker off or hop across the spine.
  const lastShownIds = useRef(new Map());
  // row owner → { step, frame, seen, sides } from that row's last pass: the zoom step and frame
  // it was laid out at, every label key it laid out, and each neutral label's last side. While
  // the geometry is unchanged (a click, a hover, any re-render that is not a zoom or pan)
  // labels keep their state — see stickyLabelMode.
  const lastLayoutRef = useRef(new Map());
  // Ghost map is managed by lib/timeline/labelLayout.updateLabelGhosts.
  const labelGhostsRef = useRef(new Map());
  // Which labels each row last DREW as labels (key → side), for the ghost transition test.
  const renderedLabelsRef = useRef(new Map());
  // Where the lane becomes visible: the left overlay panel covers the column.
  const laneLeftEdge = railPanelSettled ? railPanelWidth : 0;
  const laneRightEdge = cSize.w || 800;

  // `edges` is this row's forecast line (`capX`) and, when its outcome bars reach into the
  // label band, their left edge (`wallX`) — see renderRow.
  const computeVisibility = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- reads AppInner-owned refs and per-render helpers through the hook's argument object; the manual deps are verbatim from AppInner
    (events, spineY, forceBelow, forceAbove, triggerEvId, rowId = "main", edges = {}) => {
      if (!events.length) return [];
      // Collide as if labels were 128px (they render at 104), so shown labels
      // keep a clear gap; crowded ones fall back to dots and the signposts table names them.
      const LABEL_W = 128;
      const OVERLAP_THRESH = LABEL_W * 0.05;
      const positions = events.map((ev, i) => {
        const hasRange = isLongRange(ev);
        const anchorDate = new Date(liveAnchorRef.current.eventAnchorMs(ev, rowId));
        const sxMid = dayToScreen(anchorDate);
        const sxMin = hasRange ? dayToScreen(ev.date) : null;
        const sxMax = hasRange ? dayToScreen(ev.date_end) : null;
        let baseAbove;
        // Neutral triggers sit ON the spine, so BOTH sides of the line are free — unlike a
        // valenced trigger, whose label has to stay on the side its circle sits on. The
        // index alternation here is only the SEED: the overlap pass below picks each neutral
        // label's side by room (lib/timeline/labelLayout.sideCandidates), which fits more
        // labels before any collapses than a fixed alternation over every event does.
        if (ev._neutral) baseAbove = i % 2 === 0;
        else if (forceBelow) baseAbove = false;
        else if (forceAbove) baseAbove = true;
        else if (ev.direction === "no") baseAbove = false;
        else if (ev.direction === "yes") baseAbove = true;
        else baseAbove = i % 2 === 0;
        const promotedRank = promotedOrder.get(`${rowId}::${eventKey(ev)}`) ?? -1;
        const isPromoted = promotedRank >= 0;
        return {
          ev,
          sx: sxMid,
          // The anchor BEFORE spreadCoincidentX nudges it: that nudge is in screen px, so it must
          // never be cached as a date.
          anchorMs: ev.isPast
            ? liveAnchorRef.current.eventAnchorMs(ev, rowId, { uncapped: true })
            : anchorDate.getTime(),
          sxMin,
          sxMax,
          above: baseAbove,
          show: true,
          stagger: 0,
          compact: false,
          isPromoted,
          promotedRank,
        };
      });
      // Two triggers on the same day share an x, so their circles draw on top of each other
      // and — because the label side alternates — the pair reads as ONE circle with a label
      // out of its top and another out of its bottom. Fan a coincident cluster apart so it
      // reads as what it is. Done BEFORE the overlap pass below, which must test each label
      // against the x its circle will actually occupy, and the fork event is pinned (its
      // branch connector is drawn from the fork date, not from this layout).
      // No circle may be fanned past the row's forecast line, resolved or not: a cluster
      // sitting on it would otherwise put half its members under the outcome bars.
      const capMs = ownsResolutionCap(rowId) ? liveAnchorRef.current.resCapMs : null;
      const maxXs = [capMs != null ? dayToScreen(new Date(capMs)) : null, edges.capX].filter(
        Number.isFinite,
      );
      const spreadXs = spreadCoincidentX(
        positions.map((p) => ({ id: p.ev.id, sx: p.sx })),
        {
          anchorId: triggerEvId ?? null,
          maxX: maxXs.length ? Math.min(...maxXs) : null,
        },
      );
      // The fan can carry an occurred trigger past today; pull it back.
      const todaySx = dayToScreen(new Date(liveAnchorRef.current.todayMs));
      positions.forEach((p, i) => {
        p.sx = p.ev.isPast ? Math.min(spreadXs[i], todaySx) : spreadXs[i];
      });

      if (ppd < LABEL_MIN_PPD) {
        // No labels at this zoom: forget the row's layout, so coming back lays out afresh
        // instead of reading a zoom direction off the step it was at before.
        lastLayoutRef.current.delete(rowOwnerKey(rowId, ""));
        return positions;
      }
      // This row's newest opened label always shows (step 3 below).
      const newestRank = positions.reduce((m, p) => Math.max(m, p.promotedRank), -1);
      // Zoom STEP, not raw zoom: the layout re-runs only when zoom crosses a step, and in
      // between every label keeps its state (sticky mode), so a zoom gesture re-arranges
      // labels a few times instead of on every frame. Pan is left out on purpose — it moves
      // every label by the same px, which changes no overlap.
      // The row's resolution cap is part of it too (`frame`): a cap change (scrubbing between the
      // resolved view and a pre-resolution vintage) moves circles, so the labels must be re-laid
      // out. So is the bars' wall appearing or going: labels it closed must get a chance to
      // reopen. When only the step moved, and down, the row zoomed out: dots stay dots.
      const wallX = Number.isFinite(edges.wallX) ? edges.wallX : null;
      const step = zoomStep(ppd);
      const frame = `${ownsResolutionCap(rowId) ? (liveAnchorRef.current.resCapMs ?? "") : ""}|${wallX != null}`;
      // Owner-scoped like lastShownIds: "main" is every forecast's primary lane, so a bare
      // rowId key would hand the next forecast this one's layout.
      const layoutKey = rowOwnerKey(rowId, "");
      const prevLayout = lastLayoutRef.current.get(layoutKey);
      const sameFrame = !!prevLayout && prevLayout.frame === frame;
      const sameGeometry = sameFrame && prevLayout.step === step;
      const zoomedOut = sameFrame && Number(step) < Number(prevLayout.step);
      // A neutral label's last side, remembered while it is a dot too, so it reopens where
      // it closed instead of re-picking a side (and reshuffling its neighbours).
      const sideMemory = new Map(prevLayout?.sides ?? []);

      // Labels on opposite sides of the spine are vertically separated and never collide; see
      // lib/timeline/labelLayout.
      // Collide labels where they are DRAWN: renderEvt slides a label near the
      // left panel or the right edge (clampLabelShift), so the occupancy test does the same.
      const occBase = createLabelOccupancy({ labelW: LABEL_W, thresh: OVERLAP_THRESH, wallX });
      // A node under the panel draws no label, so it reserves no room either.
      const fitOf = (sx) => clampLabelShift(sx, 104, { minX: laneLeftEdge, maxX: laneRightEdge });
      const occ = {
        fits: (sx, above, mode) => {
          const f = fitOf(sx);
          return f.hidden || occBase.fits(sx + f.shift, above, mode);
        },
        place: (sx, above) => {
          const f = fitOf(sx);
          if (!f.hidden) occBase.place(sx + f.shift, above);
        },
        gap: (sx, above) => occBase.gap(sx + fitOf(sx).shift, above),
      };
      const hasOverlap = (sx, above, _w, loose) => !occ.fits(sx, above, loose);
      const place = (sx, above) => occ.place(sx, above);

      const order = labelPlacementOrder(
        positions.map((p) => ({
          isFork: triggerEvId != null && p.ev.id === triggerEvId,
          promotedRank: p.promotedRank,
          wasDot:
            !!prevLayout &&
            prevLayout.seen.has(eventKey(p.ev)) &&
            !lastShownIds.current.has(rowOwnerKey(rowId, eventKey(p.ev))),
          relevance: p.ev.relevance || p.ev.p_conditional || 0.5,
          sx: p.sx,
        })),
      );

      // The slide cache keeps slid positions stable across zoom.
      const trySlide = (p, above, w, fitMode) => {
        if (!p.sxMin) return false;
        // Slide no further than the resolution MARKER (`_resCapMs` once resolved), same
        // moment eventAnchorMs caps at. Only the active forecast's own rows are bounded by
        // its resolution: a compared forecast can resolve later.
        const rd = !ownsResolutionCap(rowId)
          ? Infinity
          : liveAnchorRef.current.resCapMs != null
            ? liveAnchorRef.current.resCapMs
            : active?.contract?.resolution_date
              ? new Date(active.contract.resolution_date).getTime()
              : Infinity;
        const pastCap = p.ev.isPast ? liveAnchorRef.current.todayMs : Infinity;
        const dStart = new Date(p.ev.date).getTime(),
          dEnd = Math.min(new Date(p.ev.date_end).getTime(), rd, pastCap);
        if (dEnd <= dStart) return false;
        const cached = slideAnchorMs(p.ev, rowId);
        if (cached != null) {
          const cx = dayToScreen(new Date(Math.min(cached, pastCap)));
          if (!hasOverlap(cx, above, w, fitMode)) {
            p.sx = cx;
            place(cx, above);
            return true;
          }
        }
        for (const f of [0.5, 0.35, 0.65, 0.2, 0.8]) {
          const dateMs = dStart + f * (dEnd - dStart);
          const cx = dayToScreen(new Date(dateMs));
          if (!hasOverlap(cx, above, w, fitMode)) {
            p.sx = cx;
            setSlideAnchorMs(p.ev, rowId, dateMs);
            place(cx, above);
            return true;
          }
        }
        return false;
      };

      for (const idx of order) {
        const p = positions[idx];
        // Events with server-provided mean_date are pinned to that position — never slide,
        // never cache to slideCache. Lower-relevance overlapping events fall through to dot
        // form. The branch fork "header" event is pinned the same way: it must stay
        // centered on the branch's forkDate (carried on `date` for a point fork,
        // `mean_date` for a windowed one) so the header circle, its collapse/remove
        // buttons, and the fork connector never diverge. Without the pin it picks up a
        // round-tripped slideCache entry on layout (screenToDay→day→ms loses precision)
        // that pulls the circle off forkDate. Pinned ⇒ eventAnchorMs skips slideCache and
        // resolves to forkDate via `date` for a point fork, via `mean_date` for a windowed
        // one (buildForkNode sets whichever applies, so the window survives and the node
        // still lands on the corner).
        const hasMean = !!p.ev.mean_date;
        const isTriggerEvt = triggerEvId != null && p.ev.id === triggerEvId;
        const isPinned = hasMean || isTriggerEvt;
        const isAnchored = isPinned || slideAnchorMs(p.ev, rowId) != null;
        const lastAbove = lastShownIds.current.get(rowOwnerKey(rowId, eventKey(p.ev)));
        const wasShown = lastAbove !== undefined;
        const anchorPos = () => {
          if (isPinned) return;
          if (slideAnchorMs(p.ev, rowId) == null) setSlideAnchorMs(p.ev, rowId, p.anchorMs);
        };
        // A dot last pass needs real clearance to reopen (labelLayout `enterGap`); with the
        // loose test for shown labels that is a dead band, so a label on the boundary stops
        // toggling every zoom frame.
        const wasHidden = !!prevLayout && prevLayout.seen.has(eventKey(p.ev)) && !wasShown;
        // The sides to try, best first: a valenced label's own side; a neutral one's
        // roomier side (or the side it was already shown on — hysteresis).
        // With zoom and pan unchanged, a label keeps its last state: only the newest opened
        // one is laid out, and a shown label it now overlaps closes to a dot. Nothing else
        // slides, changes side, or opens (lib/timeline/labelLayout.stickyLabelMode).
        const mode = stickyLabelMode({
          sameGeometry,
          zoomedOut,
          isNewest: p.isPromoted && p.promotedRank === newestRank,
          seen: !!prevLayout && prevLayout.seen.has(eventKey(p.ev)),
          wasShown,
        });
        if (mode === "hide") {
          p.show = false;
          continue;
        }
        if (mode === "keep") {
          p.above = lastAbove;
          if (occ.fits(p.sx, p.above, true)) place(p.sx, p.above);
          else p.show = false;
          continue;
        }
        const sides = sideCandidates({
          neutral: !!p.ev._neutral,
          fixedAbove: p.above,
          preferAbove: p.above,
          lastAbove: p.ev._neutral
            ? wasShown
              ? lastAbove
              : (sideMemory.get(eventKey(p.ev)) ?? null)
            : null,
          occ,
          sx: p.sx,
        });
        // 1. Full label at its own position, on each allowed side in turn. The loose (anti-flicker)
        // test applies only on the side it was already shown on.
        const fitSide = sides.find((above) =>
          occ.fits(
            p.sx,
            above,
            wasShown && above === lastAbove ? "loose" : wasHidden ? "strict" : false,
          ),
        );
        if (fitSide !== undefined) {
          p.above = fitSide;
          place(p.sx, fitSide);
          anchorPos();
          continue;
        }
        const slidSide = isAnchored
          ? undefined
          : // A slide is still a reopen: a dot last pass needs the same clearance here.
            sides.find((above) => trySlide(p, above, LABEL_W, wasHidden ? "strict" : false));
        if (slidSide !== undefined) {
          p.above = slidSide;
          continue;
        }
        // 3. The MOST RECENTLY OPENED label always shows — clicking a dot must open it —
        //    on its roomier side, even over an older label (only the fork header, placed
        //    first, can still sit under it). Every other open label that no longer fits
        //    closes back to a dot.
        if (p.isPromoted && p.promotedRank === newestRank) {
          p.above = sides[0];
          place(p.sx, sides[0]);
          continue;
        }
        // 4. Labels are never word-reduced; failing every option leaves the dot only.
        p.show = false;
      }
      // Hysteresis is shared across all rows (main, compare lanes, branches), so refresh only THIS
      // row-owner's keys. Replacing the whole Map would wipe the "wasShown" history of every other
      // row (each renders via its own computeVisibility call) and reintroduce label flicker in
      // compare/branch views.
      const ownerPrefix = rowOwnerKey(rowId, "");
      for (const k of [...lastShownIds.current.keys()])
        if (k.startsWith(ownerPrefix)) lastShownIds.current.delete(k);
      for (const p of positions)
        if (p.show) lastShownIds.current.set(rowOwnerKey(rowId, eventKey(p.ev)), p.above);
      for (const p of positions)
        if (p.show && p.ev._neutral) sideMemory.set(eventKey(p.ev), p.above);
      lastLayoutRef.current.set(layoutKey, {
        step,
        frame,
        seen: new Set(positions.map((p) => eventKey(p.ev))),
        sides: sideMemory,
      });
      return positions;
    },
    // promotedOrder must be a dep: open labels take part in the overlap pass, so a click has to
    // re-run it.
    [dayToScreen, ppd, promotedOrder, laneLeftEdge, laneRightEdge],
  );
  return {
    computeVisibility,
    labelGhostsRef,
    laneLeftEdge,
    laneRightEdge,
    lastLayoutRef,
    lastShownIds,
    promotedOrder,
    renderedLabelsRef,
  };
}
